// Theme — light/dark toggle persisted to localStorage and applied to <html>
// via the `data-theme` attribute. Tailwind config maps this to dark: variants.

import { useEffect, useState, useCallback } from 'react';

export type Theme = 'light' | 'dark';
const KEY = 'stratus-theme';

function applyTheme(t: Theme) {
  document.documentElement.setAttribute('data-theme', t);
}

function readInitial(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {}
  // Default: follow OS preference, fall back to dark
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readInitial);

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(KEY, theme); } catch {}
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle   = useCallback(() => setThemeState(t => t === 'dark' ? 'light' : 'dark'), []);

  return { theme, setTheme, toggle };
}

// Apply on module load so the page paints with the right colors before React mounts
if (typeof document !== 'undefined') applyTheme(readInitial());
